import { Component, AfterContentInit, ViewChild, Inject } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup, FormBuilder, Validators, FormControl, FormGroupDirective, ValidationErrors, AsyncValidatorFn, AbstractControl } from '@angular/forms';
import { HomeService, IPortingAnswer, IPortingRequest, ISimcardDetails } from 'src/app/services/home.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-request',
  templateUrl: './request.component.html',
  styleUrls: ['./request.component.scss']
})
export class RequestComponent implements AfterContentInit {
  public requestForm: FormGroup;
  public eeccOptionList: Array<string> = [];
  public errormessage?: string;
  public submitErrorCode?: string;
  public formSubscription?: Subscription;
  public waiting = false;
  public showDetailMode = false;

  private today = new Date();
  // private phonenumberPattern = /^06\d{8}$/;
  private phonenumberPattern = /^(061|062|063|064|065|068)\d{7}$/;
  private simCardPattern = /^(\d{8}|\d{13}|\d{19})$/;

  private postcodePattern = /^[1-9][0-9]{3}[\s]{0,1}(?!sa|sd|ss)[a-z]{2}$/i;
  private emailPattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  @ViewChild(FormGroupDirective) private form: FormGroupDirective;

  constructor(private transSvc: TranslateService,
    private formBuilder: FormBuilder,
    private router: Router,
    private homeSvc: HomeService,
    @Inject(APP_BASE_HREF) public baseUrl: string  ) {

    this.requestForm = this.formBuilder.group(
      {
        'request:applicantName': ['', [Validators.required]],
        'request:applicantAddress': ['', [Validators.required]],
        'request:applicantPostcode': ['', [Validators.required, Validators.pattern(this.postcodePattern)]],
        'request:applicantCity': ['', [Validators.required]],
        'request:applicantMobile': ['', [Validators.required, Validators.pattern(this.phonenumberPattern), this.checkMobileNumbers],
          [this.verifyNumberToBePorted]],
        'request:applicantEmail': ['', [Validators.required, Validators.pattern(this.emailPattern)]],
        'request:transferDate': [null, [Validators.required, this.checkTransferDate]],
        'request:lebaraMobile': [null, [Validators.required, Validators.pattern(this.phonenumberPattern), this.checkMobileNumbers],
          [this.verifyNewNumber]],
        'request:lebaraSIMCard': [null, [Validators.required]],
        'request:eeccOption': [null, [Validators.required]],
        'request:eeccConsent': [null, [Validators.requiredTrue]]
      });

  }

  ngAfterContentInit(): void {
    this.transSvc.get('request.eeccOptions').subscribe((result: Array<string>) => {
      this.eeccOptionList = result;
    });

    delete this.errormessage;
    this.today = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate());
    const firstDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + 2);
    this.transferDate?.setValue(firstDate);
    this.eeccOption?.setValue(0);

    this.homeSvc.getSimcardDetails().then((detail: ISimcardDetails) => {
      this.simCardPattern = new RegExp(detail.SimcardRegex.replace(detail.SimCardPrefix, ''));
      this.lebaraSIMCard?.addValidators(Validators.pattern(this.simCardPattern));
    }, (err: string) => {
      this.errormessage = err;
    });

    // remove consent on each change to current contract
    this.eeccOption?.valueChanges.subscribe(() => {
      this.eeccConsent?.setValue(false);
    });
  }

  public onEditClick(): void {
    event?.preventDefault();
    this.showDetailMode = true;
    this.eeccConsent?.setValue(false);
  }

  public onSubmit(): void {
    // no submit when submit is already pending
    if (this.formSubscription) {
      return;
    }
    if (this.requestForm.pending) {
      this.formSubscription = this.requestForm.statusChanges.subscribe(() => {
        this.formSubscription?.unsubscribe();
        delete this.formSubscription;
        if (this.requestForm.valid) {
          this.submit();
        }
      });
    } else {
      if (this.requestForm.valid) {
        this.submit();
      }
    }
  }

  public submit(): void {
    if (this.waiting) {
      return;
    }
    this.waiting = true;

    delete this.submitErrorCode;

    const request: IPortingRequest = {
      applicantName: this.applicantName?.value,
      applicantAddress: this.applicantAddress?.value,
      applicantPostcode: this.applicantPostcode?.value,
      applicantCity: this.applicantCity?.value,
      applicantMobile: this.applicantMobile?.value,
      applicantEmail: this.applicantEmail?.value,
      transferDate: this.transferDate?.value,
      lebaraMobile: this.lebaraMobile?.value,
      lebaraSIMCard: this.lebaraSIMCard?.value,
      eeccScheme: this.eeccOption?.value
    };

    this.homeSvc.sendRequest(request).then((response: IPortingAnswer) => {
      if (response.errorCode) {
        this.submitErrorCode = 'request.submit_' + response.errorCode;
        return;
      }

      this.homeSvc.phoneNumber = this.applicantMobile?.value;
      this.homeSvc.svSiteUrl = response.svSiteUrl;
      this.router.navigateByUrl(`/${this.homeSvc.languageCode}/done`);
    }, (err: string) => {
      alert(err);
    }).finally(() => {
      this.waiting = false;
    });
  }

  private get applicantName() {
    return this.requestForm.get('request:applicantName');
  }
  private get applicantAddress() {
    return this.requestForm.get('request:applicantAddress');
  }
  private get applicantPostcode() {
    return this.requestForm.get('request:applicantPostcode');
  }
  private get applicantCity() {
    return this.requestForm.get('request:applicantCity');
  }
  private get applicantEmail() {
    return this.requestForm.get('request:applicantEmail');
  }
  private get transferDate() {
    return this.requestForm.get('request:transferDate');
  }
  private get applicantMobile() {
    return this.requestForm.get('request:applicantMobile');
  }
  private get lebaraMobile() {
    return this.requestForm.get('request:lebaraMobile');
  }
  private get lebaraSIMCard() {
    return this.requestForm.get('request:lebaraSIMCard');
  }
  public get eeccOption() {
    return this.requestForm.get('request:eeccOption');
  }
  public get eeccConsent() {
    return this.requestForm.get('request:eeccConsent');
  }

  private verifyNumberToBePorted: AsyncValidatorFn = (control: AbstractControl): Promise<ValidationErrors | null> => {
    const promise = new Promise<ValidationErrors | null>((resolve) => {

      if (control.invalid) {
        resolve(null);
        return;
      }

      const phoneNumber = control.value;
      this.homeSvc.verifyNumberToBePorted(phoneNumber).then((reply: string) => {
        if (reply.length > 0) {
          const result: ValidationErrors = {};
          result[reply] = true;
          resolve(result);
        } else {
          resolve(null);
        }
      }, (err: string) => {
        this.errormessage = err;
      });
    });

    return promise;
  };

  private verifyNewNumber: AsyncValidatorFn = (control: AbstractControl): Promise<ValidationErrors | null> => {
    const promise = new Promise<ValidationErrors | null>((resolve) => {

      if (control.invalid) {
        resolve(null);
        return;
      }

      const phoneNumber = control.value;
      this.homeSvc.verifyNewNumber(phoneNumber, this.applicantMobile?.value).then((reply: string) => {
        if (reply.length > 0) {
          const result: ValidationErrors = {};
          result[reply] = true;
          resolve(result);
        } else {
          resolve(null);
        }
      }, (err: string) => {
        this.errormessage = err;
      });
    });

    return promise;
  };

  private checkTransferDate = (control: FormControl) => {
    const wishDate = control.value;
    if (!(wishDate instanceof Date)) {
      return { notadate: true };
    }
    if (wishDate < this.today) {
      return { notinpast: true };
    }
    const maxDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + 90);
    if (wishDate > maxDate) {
      return { nomorethen90days: true };
    }
    return null;
  };

  private checkMobileNumbers = () => {
    if (!this.requestForm) {
      return null;
    }
    if (this.lebaraMobile?.value === this.applicantMobile?.value) {
      return { 'mustdiffer': true };
    }

    this.removeError(this.applicantMobile, 'mustdiffer' );
    this.removeError(this.lebaraMobile, 'mustdiffer');
    return null;
  };

  private removeError(control: AbstractControl | null, errorCode: string): void {
    if (!control) {
      return;
    }
    const errors: any = {};
    let hasErrors = false;
    for (const code in control.errors) {
      if (code !== errorCode) {
        errors[code] = control.errors[code];
        hasErrors = true;
      }
    }
    if (hasErrors) {
      control.setErrors(errors);
    } else {
      control.setErrors(null);
    }
  }
}
